import { useNavigate } from 'react-router-dom';
import type { BulkExportCompleteWebsocketData, BulkImportCompleteWebsocketData } from '@gcv/shared';

import { useGcvWebSocket } from 'hooks';
import { getFiDispensaryStore } from 'stores/FiDispensaryStore';
import { getSnackbarStore, SnackbarSeverity } from 'stores/SnackBarStore';
import { getUserStore } from 'stores/UserStore';
import { useBank } from 'tanstack-hooks';
import { BANK_BULK_IMPORTS_KEY, MINIFIED_DISPENSARIES_KEY } from 'tanstack-hooks/keys';
import { downloadFileFromSignedUrl } from 'util/files.util';
import { queryClient } from 'util/queryClient';
import { actions as accountsActions } from '../../fi/accounts/actions';
/**
 * Websockets for the whole FI app: opens the connection and declares what happens for each
 * message action. Mounted once, high in the tree (see FiRoute), so any page benefits from it.
 *
 * Add new message handling here. Each listener runs once per message received - it does not
 * re-run when the user navigates to another page.
 */
export const useFiWebsockets = () => {
  const navigate = useNavigate();
  const userStore = getUserStore();
  const snackbarStore = getSnackbarStore();
  const dispensaryStore = getFiDispensaryStore();

  const { bank } = useBank();

  useGcvWebSocket(userStore.user.id, {
    bulk_export_complete: (message) => {
      const { s3_key } = message.data as BulkExportCompleteWebsocketData;

      downloadFileFromSignedUrl(s3_key);
      snackbarStore.showSuccessSnackbarMessage('Your documents export is now being downloaded');
    },
    bulk_import_complete: (message) => {
      const importId = (message.data as BulkImportCompleteWebsocketData).import_id;
      // Invalidate the cache to refetch the latest invited dispensaries
      queryClient.invalidateQueries({ queryKey: [MINIFIED_DISPENSARIES_KEY, bank?.id] });
      accountsActions.sortDispensaries(dispensaryStore.dispensaries);
      queryClient.invalidateQueries({ queryKey: [BANK_BULK_IMPORTS_KEY, bank?.id] });
      snackbarStore.showSnackbar(
        SnackbarSeverity.Success,
        'Your bulk import has completed. Click here to view results',
        () => {
          navigate(`/secure/fi/accounts/bulk-imports?importId=${importId}`);
        }
      );
    }
  });
};
